import React from "react";
import { CButton, CDropdown, CDropdownItem, CDropdownMenu, CDropdownToggle } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { useDeleteUser, useLiftRestriction, useRestrictUser } from "@/hooks/useUserMaster";
import { showConfirmDialog } from "@/components/contentModeration/moderationDialogs";
import { showDeleteDialog, showRestrictDialog } from "@/components/UserActionDialogs";
import { userRestriction } from "@/constants/translation/userRestriction.translation";

const tr = userRestriction;

const liftTexts = { heading: tr.lift_heading, message: tr.lift_message };

// 409 = the user is the only admin of a community/group that still has members (see accountDeletion.service.js)
const HANDOFF_BLOCKED_STATUS = 409;

/**
 * Restrict / Lift restriction / Delete actions for one user, shared by the Normal Users list, the User View
 * page and Reported Users so the three behave identically. Deactivate stays with the caller (`onDeactivate`),
 * because each page already has its own flow for it.
 *
 * @param {Object} props
 * @param {string} props.uuid - User UUID.
 * @param {boolean} props.isRestricted - Whether the user is currently restricted.
 * @param {boolean} [props.isDeleted] - Deleted users have no actions.
 * @param {function} [props.onDeactivate] - Renders a Deactivate/Activate item when given.
 * @param {string} [props.deactivateLabel] - Label for that item.
 * @param {function} props.onChanged - Called after a successful action so the page can refetch.
 * @param {boolean} [props.showDelete=true] - Reported Users only needs Restrict / Lift.
 */
const UserActionsMenu = ({
  uuid,
  isRestricted,
  isDeleted = false,
  onDeactivate,
  deactivateLabel,
  onChanged,
  showDelete = true,
}) => {
  const t = useTranslationContext();
  const { mutate: restrict } = useRestrictUser(onChanged);
  const { mutate: liftRestriction } = useLiftRestriction(onChanged);
  const { mutate: deleteUser } = useDeleteUser(onChanged, (error) => {
    const status = error?.response?.status;
    toast.error(status === HANDOFF_BLOCKED_STATUS ? t(tr.delete_handoff_blocked) : error?.response?.data?.message || error?.message);
  });

  if (isDeleted) return null;

  const canRestrict = !isRestricted;

  return (
    <CDropdown alignment="end" portal>
      <CDropdownToggle
        color="secondary"
        caret={false}
        title={t(tr.actions_menu)}
        style={{ backgroundColor: "#212631", borderColor: "#212631", color: "white", padding: "0.25rem 0.5rem" }}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </CDropdownToggle>
      <CDropdownMenu>
        {onDeactivate && <CDropdownItem className="cursor-pointer" onClick={onDeactivate}>{deactivateLabel}</CDropdownItem>}
        {canRestrict && (
          <CDropdownItem
            className="cursor-pointer"
            onClick={() => showRestrictDialog({ t, onConfirm: (payload) => restrict({ id: uuid, ...payload }) })}>
            {t(tr.restrict_action)}
          </CDropdownItem>
        )}
        {isRestricted && (
          <CDropdownItem
            className="cursor-pointer"
            onClick={() => showConfirmDialog({ t, texts: liftTexts, onConfirm: () => liftRestriction(uuid) })}
          >
            {t(tr.lift_restriction_action)}
          </CDropdownItem>
        )}
        {showDelete && (
          <CDropdownItem
            className="text-danger cursor-pointer"
            onClick={() => showDeleteDialog({ t, onConfirm: (reason) => deleteUser({ id: uuid, reason }) })}
          >
            {t(tr.delete_action)}
          </CDropdownItem>
        )}
      </CDropdownMenu>
    </CDropdown>
  );
};

export default UserActionsMenu;
